/**
 * Game loop manager class using requestAnimationFrame
 */
export class GameLoopManager {
  private animationId: number | null = null;
  private isRunning: boolean = false;
  private lastTime: number = 0;
  private callback: (deltaTime: number) => void;

  constructor(callback: (deltaTime: number) => void) {
    this.callback = callback;
    this.gameLoop = this.gameLoop.bind(this);
  }

  private gameLoop(currentTime: number): void {
    if (!this.isRunning) return;

    const deltaTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    this.callback(deltaTime);

    this.animationId = requestAnimationFrame(this.gameLoop);
  }

  /**
   * Start the game loop
   */
  public start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastTime = performance.now();
    this.animationId = requestAnimationFrame(this.gameLoop);
  }

  /**
   * Stop the game loop
   */
  public stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Check if the game loop is running
   */
  public getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Update the callback function
   */
  public setCallback(callback: (deltaTime: number) => void): void {
    this.callback = callback;
  }
}

/**
 * Simple frame-based game loop (alternative to time-based)
 */
export class SimpleGameLoop {
  private intervalId: number | null = null;
  private isRunning: boolean = false;
  private callback: () => void;
  private fps: number;

  constructor(callback: () => void, fps: number = 60) {
    this.callback = callback;
    this.fps = fps;
  }

  /**
   * Start the game loop
   */
  public start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.intervalId = window.setInterval(this.callback, 1000 / this.fps);
  }

  /**
   * Stop the game loop
   */
  public stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Check if the game loop is running
   */
  public getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Update the callback function
   */
  public setCallback(callback: () => void): void {
    this.callback = callback;
  }

  /**
   * Update the target FPS
   */
  public setFPS(fps: number): void {
    this.fps = fps;
    if (this.isRunning) {
      this.stop();
      this.start();
    }
  }
}

/**
 * Hook-like interface for game loop (to be used with React hooks when available)
 */
export function createGameLoopHook() {
  return {
    // This will be implemented when React hooks are available
    useGameLoop: (_callback: () => void, _fps?: number) => ({
      start: () => {},
      stop: () => {},
      isRunning: false
    })
  };
} 